<w-page [mode]="PageMode.Page" [hideFooter]="true" class="settings-frame">

  <ng-container *w-page-header-title>
    @if ($breadcrumbs().length > 0) {
      <nav class="settings-breadcrumb" aria-label="Breadcrumb">
        @for (crumb of $breadcrumbs(); track $index; let last = $last) {
          @if (!last && crumb.action) {
            <button type="button" class="crumb crumb-link" (click)="crumb.action()">{{ crumb.label }}</button>
          } @else if (crumb.routerLink && !last) {
            <a class="crumb crumb-link" [routerLink]="crumb.routerLink">{{ crumb.label }}</a>
          } @else {
            <span class="crumb" [class.crumb-current]="last">{{ crumb.label }}</span>
          }
          @if (!last) {
            <span class="crumb-separator" aria-hidden="true">/</span>
          }
        }
      </nav>
    }
  </ng-container>

  <ng-container *w-page-header-actions>
    <div class="settings-header-actions">
      <wb-surface-save-status />
      <div class="settings-search" role="search">
        <i class="fa-regular fa-magnifying-glass search-icon" aria-hidden="true"></i>
        <input
          #searchBox
          type="search"
          class="search-input"
          placeholder="Search"
          autocomplete="off"
          spellcheck="false"
          aria-label="Search"
          (input)="onSearchInput($event)"
          (keydown)="onSearchKeydown($event)"
        />
      </div>
      @if ($presenceRoom) {
        <w-room-presence [room]="$presenceRoom" />
      }
      @if ($actionCommands().length > 0) {
        <wb-action-bar [commands]="$actionCommands()" />
      }
    </div>
  </ng-container>

  @if ($banner(); as banner) {
    <ng-container *w-page-alert>
      <ng-container [ngComponentOutlet]="banner.component" [ngComponentOutletInputs]="banner.inputs?.() ?? {}" />
    </ng-container>
  }

  <ng-container *w-page-body-content>
    <div class="settings-scroll">
      <div class="settings-content">
        <main>
          @if ($surfaceErrors().length > 0) {
            <div class="settings-errors" role="alert">
              @for (error of $surfaceErrors(); track error.code) {
                <p>{{ error.message }}</p>
              }
            </div>
          }

          @if ($pageTitle(); as title) {
            <h1 #pageTitle class="page-title" tabindex="-1">{{ title }}</h1>
          }

          @if ($searching()) {
            <div class="search-results" aria-live="polite">
              @if ($searchHits().length === 0) {
                <p class="search-empty">No matching properties</p>
              } @else {
                @for (area of $searchHits(); track area.path) {
                  <section class="settings-section search-area">
                    <header class="search-area-header">
                      <h2 class="page-title" [innerHTML]="area.title | settingsSearchHighlight:$searchQuery()"></h2>
                    </header>
                    @for (section of area.sections; track section.path) {
                      <section class="settings-section content-section">
                        @if (section.title && section.path !== area.path) {
                          <header class="section-header">
                            <div class="section-heading">
                              <h2 [innerHTML]="section.title | settingsSearchHighlight:$searchQuery()"></h2>
                            </div>
                          </header>
                        }
                        <w-content-list [showHeader]="false">
                          @for (row of section.fields; track row.key) {
                            @if (!row.declaration.instance?.$hidden()) {
                              <w-list-item [class.wide-editor]="row.wide" [header]="row.label | settingsSearchHighlight:$searchQuery()" [description]="row.description | settingsSearchHighlight:$searchQuery()" [action]="searchEditor" />
                              <ng-template #searchEditor>
                                <div class="settings-editor" role="group" [attr.aria-label]="row.label + (row.declaration.field.$required() ? ' (required)' : '')">
                                  <wb-field [field]="row.declaration.field" [metadata]="row.editorMetadata" [instance]="row.declaration.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" errorDisplay="overlay" [hostShowsDescription]="true" [customRenderer]="row.declaration.customRenderer" [customRendererInputs]="row.declaration.customRendererInputs" />
                                </div>
                              </ng-template>
                            }
                          }
                        </w-content-list>
                      </section>
                    }
                  </section>
                }
              }
            </div>
          } @else {
            @if (!$selectedNavigation() && $navigation().length > 0) {
              <nav class="settings-navigation" aria-label="Settings sections">
                @for (group of $navigationGroups(); track group.key) {
                  <section class="settings-section">
                    @if (group.title) {
                      <header class="section-header">
                        <div class="section-heading">
                          <h2>{{ group.title }}</h2>
                        </div>
                      </header>
                    }
                    <w-content-list [showHeader]="false">
                      @for (section of group.items; track section.info.section.path) {
                        <w-list-item
                          #navigationItem
                          class="navigation-item"
                          role="button"
                          tabindex="0"
                          [attr.aria-label]="section.info.$title() ?? section.info.section.path"
                          [icon]="section.info.section.icon ?? null"
                          [header]="section.info.$title() ?? section.info.section.path"
                          [description]="section.info.section.description ?? ''"
                          chevron="pi pi-angle-right"
                          (click)="openNavigation(section)"
                          (keydown.enter)="openNavigation(section)"
                          (keydown.space)="openNavigation(section); $event.preventDefault()"
                        />
                      }
                    </w-content-list>
                  </section>
                }
              </nav>
            }

            @for (section of $displayContent(); track section.info.section.path) {
              <section class="settings-section content-section">
                @if ((section.info.$title() || section.info.section.description) && section.info.section.path !== $selectedNavigation()?.info.section.path) {
                  <header class="section-header">
                    @if (section.info.section.icon) {
                      <span class="section-icon" aria-hidden="true"><i [class]="section.info.section.icon"></i></span>
                    }
                    <div class="section-heading">
                      @if (section.info.$title()) {
                        <h2>{{ section.info.$title() }}</h2>
                      }
                      @if (section.info.section.description) {
                        <p>{{ section.info.section.description }}</p>
                      }
                    </div>
                  </header>
                }

                @if (section.info.spacer) {
                  <div class="section-spacer" aria-hidden="true"></div>
                } @else if (section.info.customRenderer || section.info.fieldGroups.length > 0 || section.info.section.related) {
                  <div class="custom-section-card">
                    <wb-section [path]="section.info.section.path" [columns]="section.info.section.columns" [collapsible]="section.info.section.collapsible" [readonly]="section.info.section.readonly" [fields]="section.info.fields" [fieldGroups]="section.info.fieldGroups" [related]="section.info.section.related" [repeats]="section.info.repeats" [resolver]="section.info.resolver" [customRenderer]="section.info.customRenderer" [customRendererInputs]="section.info.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                  </div>
                } @else {
                  @if (sectionErrors(section).length > 0) {
                    <div class="section-errors" role="alert">
                      @for (err of sectionErrors(section); track err.code) {
                        <p class="section-error-line">{{ err.message }}</p>
                      }
                    </div>
                  }
                  @if (section.rows.length > 0) {
                    <w-content-list [showHeader]="false">
                      @for (row of section.rows; track row.key) {
                        @if (!row.declaration.instance?.$hidden()) {
                          <w-list-item [class.wide-editor]="row.wide" [header]="row.label" [description]="row.description" [action]="editor" />
                          <ng-template #editor>
                            <div class="settings-editor" role="group" [attr.aria-label]="row.label + (row.declaration.field.$required() ? ' (required)' : '')">
                              <wb-field [field]="row.declaration.field" [metadata]="row.editorMetadata" [instance]="row.declaration.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" errorDisplay="overlay" [hostShowsDescription]="true" [customRenderer]="row.declaration.customRenderer" [customRendererInputs]="row.declaration.customRendererInputs" />
                            </div>
                          </ng-template>
                        }
                      }
                    </w-content-list>
                  }
                  @if ($page(); as page) {
                    @for (repeat of section.info.repeats; track repeat.path) {
                      <div class="settings-repeat">
                        <wb-repeat-table [config]="repeatTableConfig(repeat)" [page]="page" [resolver]="section.info.resolver" [surfaceMode]="$surfaceMode()" />
                      </div>
                    }
                  }
                }
              </section>
            }
          }
        </main>
      </div>
    </div>
  </ng-container>

</w-page>
